import { createContext, useContext } from "react";
import { SAMPLE_DATA } from "@/config/env";
import {
    analyticsConfigured,
    analyticsActive,
    allowAnalytics,
    identify,
    trackPageview,
} from "@/lib/analytics";

export interface AnalyticsChoice {
    /** Asks the analytics question again, for a person changing their mind. */
    open: () => void;
}

/**
 * The analytics question, given by ConsentGate or VisitorConsent. Null
 * where it cannot be asked: the legal pages, or a visitor whose browser
 * sends Global Privacy Control.
 */
export const AnalyticsChoiceContext = createContext<AnalyticsChoice | null>(null);

export function useAnalyticsChoice(): AnalyticsChoice | null {
    return useContext(AnalyticsChoiceContext);
}

/**
 * Whether there is anything to ask about: a build with an analytics key.
 * The sample asks too, so the question can be seen, and sends nothing.
 */
export function analyticsAskable(): boolean {
    return analyticsConfigured() || SAMPLE_DATA;
}

/**
 * Analytics on, for a reason given (`record` or `choice`, see
 * allowAnalytics), with session replay only when `recording` (a yes). The
 * first time it comes on in a page it learns who this is, when someone is
 * signed in, and counts the page it is on.
 */
export function turnAnalyticsOn(
    by: "record" | "choice",
    userId: string | null,
    recording = true,
): void {
    const was = analyticsActive();
    allowAnalytics(by, recording);
    if (was || !analyticsActive()) return;
    if (userId) identify(userId);
    trackPageview(window.location.pathname, window.location.origin);
}
